import React, { useState, useEffect } from 'react';
import voiceService from '../services/voiceService';
import '../styles/VoiceControls.css';

const VoiceControls = ({ text, isVisible = true }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [settings, setSettings] = useState({
    rate: 0.8,
    pitch: 1.0,
    volume: 1.0
  });
  const [showSettings, setShowSettings] = useState(false);
  const [englishVoices, setEnglishVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(null);

  useEffect(() => {
    // Check if speech synthesis is supported
    setIsSupported(voiceService.isSupported());
    
    // Get English voices
    const loadVoices = () => {
      const voices = voiceService.getEnglishVoices();
      setEnglishVoices(voices);
      if (voices.length > 0 && !selectedVoice) {
        setSelectedVoice(voices[0]);
      }
    };

    // Load voices immediately or wait for them to load
    if (voiceService.getAvailableVoices().length > 0) {
      loadVoices();
    } else {
      // Wait for voices to load
      const checkVoices = setInterval(() => {
        if (voiceService.getAvailableVoices().length > 0) {
          loadVoices();
          clearInterval(checkVoices);
        }
      }, 100);

      // Cleanup interval after 5 seconds
      setTimeout(() => clearInterval(checkVoices), 5000);
    }

    // Listen for voice status changes
    const updateStatus = () => {
      const status = voiceService.getStatus();
      setIsPlaying(status.isPlaying);
      setIsPaused(status.isPaused);
    };

    // Update status periodically
    const statusInterval = setInterval(updateStatus, 100);

    return () => {
      clearInterval(statusInterval);
    };
  }, [selectedVoice]);

  const handlePlayPause = async () => {
    if (!isSupported || !text) return;

    try {
      if (isPlaying && !isPaused) {
        voiceService.pause();
      } else if (isPaused) {
        voiceService.resume();
      } else {
        // Update voice settings before speaking
        voiceService.updateSettings({
          ...settings,
          voice: selectedVoice
        });
        
        await voiceService.speak(text, {
          rate: settings.rate,
          pitch: settings.pitch,
          volume: settings.volume,
          voice: selectedVoice
        });
      }
    } catch (error) {
      console.error('Voice playback error:', error);
    }
  };

  const handleStop = () => {
    voiceService.stop();
  };

  const handleSettingsChange = (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    voiceService.updateSettings(newSettings);
  };

  const handleVoiceChange = (voiceName) => {
    const voice = englishVoices.find(v => v.name === voiceName);
    setSelectedVoice(voice);
    voiceService.updateSettings({ voice });
  };

  if (!isVisible || !isSupported) {
    return null;
  }

  return (
    <div className="voice-controls">
      <div className="voice-controls-main">
        <button
          onClick={handlePlayPause}
          className={`voice-button play-pause ${isPlaying ? (isPaused ? 'paused' : 'playing') : ''}`}
          disabled={!text}
          title={isPlaying ? (isPaused ? 'Resume' : 'Pause') : 'Play'}
        >
          {isPlaying ? (isPaused ? '▶️' : '⏸️') : '🔊'}
        </button>
        
        <button
          onClick={handleStop}
          className="voice-button stop"
          disabled={!isPlaying}
          title="Stop"
        >
          ⏹️
        </button>
        
        <button
          onClick={() => setShowSettings(!showSettings)}
          className="voice-button settings"
          title="Voice Settings"
        >
          ⚙️
        </button>
      </div>

      {showSettings && (
        <div className="voice-settings">
          <div className="settings-section">
            <label>Voice Selection:</label>
            <select
              value={selectedVoice?.name || ''}
              onChange={(e) => handleVoiceChange(e.target.value)}
              className="voice-select"
            >
              {englishVoices.map((voice) => (
                <option key={voice.name} value={voice.name}>
                  {voice.name} ({voice.lang})
                </option>
              ))}
            </select>
          </div>

          <div className="settings-section">
            <label>Speech Rate: {settings.rate.toFixed(1)}x</label>
            <input
              type="range"
              min="0.5"
              max="2.0"
              step="0.1"
              value={settings.rate}
              onChange={(e) => handleSettingsChange('rate', parseFloat(e.target.value))}
              className="settings-slider"
            />
          </div>

          <div className="settings-section">
            <label>Pitch: {settings.pitch.toFixed(1)}</label>
            <input
              type="range"
              min="0.5"
              max="2.0"
              step="0.1"
              value={settings.pitch}
              onChange={(e) => handleSettingsChange('pitch', parseFloat(e.target.value))}
              className="settings-slider"
            />
          </div>

          <div className="settings-section">
            <label>Volume: {Math.round(settings.volume * 100)}%</label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={settings.volume}
              onChange={(e) => handleSettingsChange('volume', parseFloat(e.target.value))}
              className="settings-slider"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default VoiceControls;
